/**
 * Public-facing display label for a dataset variable (#23).
 *
 * The client's requirement is that a configured public name is presented *exactly* as
 * configured — `NO2_umol_l` must never become `No2_umol_l`. Before this resolver the app
 * ran variable labels through Angular's `titlecase` pipe and through
 * `charAt(0).toUpperCase() + slice(1)`, both of which rewrite the caller's casing.
 *
 * The rules, in order:
 *
 *   1. A non-empty configured `publicTitle` is returned verbatim — no case change, no
 *      trimming of the returned value, no underscore splitting, no Unicode normalisation.
 *   2. A missing, empty or whitespace-only `publicTitle` falls back to the *unchanged*
 *      technical identifier. The fallback is a preservation rule, not a prettify rule.
 *
 * Technical identifiers stay the storage/query/API keys throughout; this function is a
 * presentation concern only and never feeds a request payload.
 *
 * Deliberately pure and framework-free so it can be unit-tested directly and shared by
 * the pipe, the chart component and the CSV/PNG export paths without duplicating logic.
 */

/** Shape of one entry in the `/api/variables-public-names` response, keyed by technical key. */
export interface VariablePublicName {
  publicTitle?: string;
  internationalTitle?: string;
}

/** The `/api/variables-public-names` response: technical key -> configured titles. */
export type VariablePublicNamesMap = Record<string, VariablePublicName>;

export function resolveVariableDisplayName(
  technicalKey: string,
  publicNames: VariablePublicNamesMap | null | undefined,
): string {
  if (!technicalKey) {
    return technicalKey;
  }

  const publicTitle = publicNames?.[technicalKey]?.publicTitle;

  // `public_title` is optional and unvalidated in Admin (schema default ""), so an entry
  // existing is not the same as a name being configured — test the value, not the record.
  if (typeof publicTitle === 'string' && publicTitle.trim() !== '') {
    return publicTitle;
  }

  return technicalKey;
}
